import React, { useCallback } from 'react';
import { View, Pressable, ActivityIndicator, FlatList } from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import CustomTextElipses from '../CustomTextElipses';
import Checkbox from '../Checkbox';
import StatusBadge from '../StatusBadge';
import webStyles from '../../style/webStyles';
import Util from '../../util/util';
import { useResponsiveStyles } from '../../hooks';
import AppStyles from '../../AppStyles';
import { t as translate, useTranslation } from '../../i18n';

const formatCountdown = (ms) => {
  if (ms <= 0) return '<1h';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  return `${days}d ${hours % 24}h`;
};

/**
 * Single report item component for FlatList rendering
 */
const ReportItem = React.memo(({
  element,
  index,
  totalReports,
  defaultOrganization,
  userAdmin,
  hasComments,
  isClosed,
  onReportPress,
  scale,
  isWeb,
  isNarrow,
  showOrganization,
  selectMode,
  isSelected,
  onToggleSelect,
  isUnread,
}) => {
  const { t } = useTranslation('reports');

  return (
  <Pressable
    onPress={() => {
      if (selectMode) {
        onToggleSelect(element.uuid);
        return;
      }
      const reportData = {
        ...element,
        index: totalReports - index,
        defaultOrganization,
        userAdmin,
      };
      onReportPress(reportData);
    }}
    style={isWeb ? { cursor: 'pointer' } : undefined}
  >
    {({ hovered, pressed, focused }) => (
    <View
      style={[
        webStyles.reportContainer,
        (hovered || focused) && webStyles.reportContainerHovered,
        pressed && webStyles.reportContainerPressed,
      ]}
    >
      {/* Status icon or checkbox */}
      <View style={webStyles.reportCheck}>
        {selectMode ? (
          <Checkbox
            selected={isSelected}
            onChangeValue={() => onToggleSelect(element.uuid)}
            label={t('list.selectReportLabel', { id: Util.formatUuid(element.uuid) })}
          />
        ) : (
          <>
            {isUnread && (
              <View
                accessibilityLabel={t('list.unreadLabel')}
                style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: AppStyles.colorSet.brandPrimary, marginVertical: 3 }}
              />
            )}
            {element.isUrgent && !hasComments(element) && !isClosed(element) && (
              <Feather name="alert-circle" size={20 * scale} color={AppStyles.colorSet.error} />
            )}
            {hasComments(element) && !isClosed(element) && (
              <Feather name="message-circle" size={20 * scale} color={AppStyles.colorSet.brandPrimary} />
            )}
            {isClosed(element) && (
              <Feather name="check-circle" size={20 * scale} color={AppStyles.colorSet.success} />
            )}
          </>
        )}
      </View>
      {/* Report content */}
      <View style={webStyles.reportInfoContainer}>
        <View style={[webStyles.reportInfo, isNarrow && webStyles.reportInfoNarrow]}>
          <CustomText style={webStyles.reportInfoItem} font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
            {t('list.reportNumber', { id: Util.formatUuid(element.uuid) })}
          </CustomText>
          <CustomText style={webStyles.reportInfoItem} font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
            {Util.timeConverter(element.timestamp)}
          </CustomText>
          {element.status && (
            <StatusBadge status={element.status} style={isNarrow ? undefined : { marginLeft: 8 }} compact />
          )}
        </View>
        {showOrganization && element.organization && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <Feather name="briefcase" size={12} color={AppStyles.colorSet.textMuted} />
            <CustomText font="regular" size={12} color={AppStyles.colorSet.textSecondary}>
              {element.organization}
            </CustomText>
          </View>
        )}
        {element.assignedTo && element.assignedTo !== 'UNASSIGNED' && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <Feather name="user" size={12} color={AppStyles.colorSet.textMuted} />
            <CustomText font="regular" size={12} color={AppStyles.colorSet.textSecondary}>
              {element.assignedTo}
            </CustomText>
          </View>
        )}
        {element.status !== 'Closed' && (() => {
          const level = element.escalationLevel || 0;
          const isEscalated = level > 0;
          const isMaxLevel = level >= 2;
          const hasEscalationTiming = element.nextEscalationAt && element.escalationWindowMs > 0;
          const hasReminderTiming = isMaxLevel && element.nextReminderAt && element.reminderWindowMs > 0;
          const hasTiming = hasEscalationTiming || hasReminderTiming;
          if (!isEscalated && !hasTiming) return null;

          let remaining, isPast, pct;
          if (hasReminderTiming) {
            remaining = element.nextReminderAt - Date.now();
            isPast = remaining <= 0;
            pct = 1 - (remaining / element.reminderWindowMs);
          } else if (hasEscalationTiming) {
            remaining = element.nextEscalationAt - Date.now();
            isPast = remaining <= 0;
            pct = 1 - (remaining / element.escalationWindowMs);
          } else {
            remaining = 0;
            isPast = false;
            pct = 0;
          }
          if (!isEscalated && !isPast && pct < 0.5) return null;

          // Max-level tickets show dark red, overdue shows red, approaching shows orange/gold
          const color = isMaxLevel ? '#991B1B'
            : isPast ? '#DC2626'
            : pct >= 0.75 ? '#EA580C'
            : isEscalated ? '#DC2626'
            : '#D97706';

          // "Overdue" only when the window was genuinely missed (past by more than
          // one full interval). Otherwise the scheduled task just hasn't run yet.
          const windowMs = hasReminderTiming ? element.reminderWindowMs : (element.escalationWindowMs || 0);
          const isOverdue = isPast && Math.abs(remaining) > windowMs;

          // This badge sits outside the component, so it resolves through the
          // module-level `translate`. Each fragment is its own whole phrase —
          // the middot only joins them, it is not part of any sentence.
          const joinDot = (head, tail) => `${head} \u00b7 ${tail}`;

          let label = '';
          if (isMaxLevel) {
            label = translate('reports:list.escalatedLevel', { level });
            if (element.remindersCapped) {
              label = joinDot(label, translate('reports:list.maxRemindersReached'));
            } else if (hasReminderTiming) {
              label = joinDot(
                label,
                isOverdue
                  ? translate('reports:list.reminderOverdue')
                  : translate('reports:list.reminderIn', { time: formatCountdown(remaining) }),
              );
            }
          } else if (isEscalated) {
            label = translate('reports:list.escalatedLevel', { level });
            if (hasEscalationTiming) {
              label = joinDot(
                label,
                isOverdue
                  ? translate('reports:list.overdue')
                  : translate('reports:list.escalatesIn', { time: formatCountdown(remaining) }),
              );
            }
          } else if (isOverdue) {
            label = translate('reports:list.escalationOverdue');
          } else if (hasTiming) {
            label = translate('reports:list.escalatesIn', { time: formatCountdown(remaining) });
          }
          if (!label) return null;

          return (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <Feather name="arrow-up-circle" size={12} color={color} />
              <CustomText font="medium" size={12} color={color}>
                {label}
              </CustomText>
            </View>
          );
        })()}
        <CustomTextElipses
          numberOfLines={2}
          ellipsizeMode="tail"
          style={webStyles.report}
          font={isUnread ? 'semiBold' : 'medium'}
          size={16 * scale}
          color={AppStyles.colorSet.textPrimary}
        >
          {Util.formatDescription(element.content)}
        </CustomTextElipses>
      </View>
      {/* Chevron */}
      <Feather name="chevron-right" size={20} color={AppStyles.colorSet.textMuted} />
    </View>
    )}
  </Pressable>
  );
});

/**
 * ReportList component - displays a list of reports using FlatList for performance
 * Supports infinite scroll via onLoadMore callback
 */
const ReportList = ({
  reports,
  reportsLoading,
  defaultOrganization,
  userAdmin,
  hasComments,
  isClosed,
  onReportPress,
  showOrganization = false,
  // Infinite scroll props
  onLoadMore,
  loadingMore = false,
  hasMore = false,
  // Bulk select props
  selectMode = false,
  selectedUuids = [],
  onToggleSelect,
  // Viewed/unread tracking: (uuid) => boolean, undefined disables unread dots
  isViewed,
}) => {
  const { t } = useTranslation('reports');
  const { isWeb, screenWidth } = useResponsiveStyles();

  // Re-render rows when selection or the viewed set changes (stable identity otherwise)
  const listExtraData = React.useMemo(() => ({ selectedUuids, isViewed }), [selectedUuids, isViewed]);

  // Adjust scale for small web screens
  const scale = isWeb && screenWidth < 800 ? 0.7 : 1;
  // Narrow (phone-width) cards can't fit Report #, timestamp, and the status
  // badge on one row — let that row wrap so the badge doesn't collide.
  const isNarrow = screenWidth < 600;

  // Render individual report item
  // Note: isSelected uses selectedUuids which changes on every toggle.
  // ReportItem is React.memo'd so only items whose isSelected actually changed will re-render.
  // selectedUuids is passed via FlatList extraData to trigger re-renders when selection changes.
  const renderItem = useCallback(({ item: element, index }) => (
    <ReportItem
      element={element}
      index={index}
      totalReports={reports.length}
      defaultOrganization={defaultOrganization}
      userAdmin={userAdmin}
      hasComments={hasComments}
      isClosed={isClosed}
      onReportPress={onReportPress}
      scale={scale}
      isWeb={isWeb}
      isNarrow={isNarrow}
      showOrganization={showOrganization}
      selectMode={selectMode}
      isSelected={selectedUuids.includes(element.uuid)}
      onToggleSelect={onToggleSelect}
      isUnread={isViewed ? !isViewed(element.uuid) : false}
    />
  ), [reports?.length, defaultOrganization, userAdmin, hasComments, isClosed, onReportPress, scale, isWeb, isNarrow, showOrganization, selectMode, selectedUuids, onToggleSelect, isViewed]);

  // Render loading state
  const renderLoading = useCallback(() => (
    <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />
  ), []);

  // Render empty state
  const renderEmpty = useCallback(() => {
    if (reportsLoading) return null;
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 60 }}>
        <Feather name="inbox" size={48} color={AppStyles.colorSet.textMuted} />
        <CustomText
          font="medium"
          size={18}
          color={AppStyles.colorSet.textSecondary}
          style={{ marginTop: 16, textAlign: 'center' }}
        >
          {t('list.empty')}
        </CustomText>
        <CustomText
          font="regular"
          size={14}
          color={AppStyles.colorSet.textMuted}
          style={{ marginTop: 8, textAlign: 'center' }}
        >
          {t('list.emptyHint')}
        </CustomText>
      </View>
    );
  }, [reportsLoading]);

  // Key extractor for FlatList
  const keyExtractor = useCallback((item) => item.uuid, []);

  // Footer component for loading more indicator
  const renderFooter = useCallback(() => {
    if (!loadingMore) return null;
    return (
      <View style={{ padding: 16, alignItems: 'center' }}>
        <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
        <CustomText font="regular" size={12} color={AppStyles.colorSet.textMuted} style={{ marginTop: 8 }}>
          {t('list.loadingMore')}
        </CustomText>
      </View>
    );
  }, [loadingMore]);

  // Handle end reached for infinite scroll
  const handleEndReached = useCallback(() => {
    if (onLoadMore && hasMore && !loadingMore && !reportsLoading) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loadingMore, reportsLoading]);

  if (reportsLoading || reports == null) {
    return renderLoading();
  }

  return (
    <FlatList
      data={reports}
      extraData={listExtraData}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ListEmptyComponent={renderEmpty}
      ListFooterComponent={renderFooter}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.3}
      initialNumToRender={10}
      maxToRenderPerBatch={10}
      windowSize={5}
      removeClippedSubviews={true}
      style={{ paddingTop: hp(1), maxHeight: '100vh', width: '100%' }}
    />
  );
};

export default ReportList;
